/**
 * document-core-ref-columns.tsx — kolumny core WSPÓLNE dla wszystkich 7 list dokumentów
 * (2 klasy faktur + 5 klas STANDARD: umowy/pisma/handlowe/wewnętrzne/specjalne).
 *
 * Wyekstrahowane z bloków kolumn `owner_user_id`/`source` skopiowanych 1:1 do
 * PurchaseInvoices/SaleInvoices/Contracts/Letters/CommercialDocs/InternalDocs (epika
 * picker+ext, znalezisko weryfikatora [I]) — jedno źródło prawdy zamiast 6 kopii tego
 * samego kodu. Kolumna „kategoria" (stary mechanizm `primary_category_id`, ADR-0029) usunięta
 * z obszaru Dokumenty 2026-08-26 (ADR-0095b). Odtworzona NOWYM mechanizmem (`record_categories`,
 * ADR-0093b) — ale NIE tutaj: `buildCategoryColumn` w `@/lib/ext-list-columns` (współdzielona
 * z Kontrahentami, jedno źródło prawdy zamiast per-obszar buildera), wołana wprost przez każdą
 * z 7 stron list obok `buildCompanyColumn`, poza tym builderem.
 *
 * `withSource` — TYLKO 5 klas STANDARD mają kolumnę „źródło" na liście. Faktury renderują
 * źródło OSOBNO przez `SourceBadge`/`SaleSourceBadge` (słownik manual/symfonia/ksef,
 * integracja Symfonia/KSeF) — inny słownik niż `documents.source` wolnego tekstu/enumu
 * frontu (manual/import/api) tych 5 klas, więc PurchaseInvoices/SaleInvoices NIE wołają
 * tego buildera z `withSource: true` (mają własną kolumnę `source` niezależnie).
 */

import type { ColumnDefinition } from '@/components/configurable-table';
import {
  codeFilterOptions,
  type ExtRefLabels,
  type ListFilterOption,
  renderCoreReferenceCell,
} from '@/lib/ext-list-columns';

/**
 * Wartości `documents.source` obsłużone przez enum frontu — LUSTRO `DOC_SOURCES` z
 * `documentCoreRenderers.tsx` (formularz dokumentu handlowego/wewnętrznego), trzymane tu, żeby
 * builder kolumn nie ciągnął modułu formularza; test `document-core-ref-columns.test.tsx` pilnuje
 * zgodności obu list. Kontrakt umów jest WOLNYM TEKSTEM
 * (`contractCoreRenderers.tsx` → `sourceRenderer`, brak selecta) — wartości spoza tego
 * zbioru (dowolny tekst wpisany ręcznie) renderują się jako SUROWA wartość BEZ wywołania
 * `t()`: klucz i18n zbudowany z dowolnego wolnego tekstu może kolidować z separatorem
 * kropkowym i18next i/lub przeciekać jako surowy klucz zamiast tłumaczenia na ekran.
 */
export const DOC_SOURCE_VALUES = new Set<string>(['manual', 'import', 'api']);

/**
 * Etykieta wartości `documents.source` — JEDNO miejsce mapowania kod → tekst: używa go komórka
 * kolumny „źródło" ORAZ opcje listy wyboru jej filtra, więc filtr i wiersze mówią to samo. Wartość
 * spoza enumu (wolny tekst umowy) zwraca SUROWO, bez `t()` (patrz `DOC_SOURCE_VALUES`).
 */
function sourceLabel(raw: string, t: (key: string) => string): string {
  return DOC_SOURCE_VALUES.has(raw) ? t(`tenant.documents.shared.sourceValues.${raw}`) : raw;
}

export interface BuildDocumentCoreRefColumnsArgs {
  t: (key: string) => string;
  /** Getter stabilny `ExtRefLabels` — patrz `useExtRefLabelsGetter` (`@/lib/ext-list-columns`). */
  getRefLabels: (() => ExtRefLabels | undefined) | undefined;
  /** Dołącz kolumnę „źródło" — WYŁĄCZNIE 5 klas STANDARD, patrz nagłówek pliku. */
  withSource?: boolean;
  /**
   * Sortowalność serwerowa kolumny „źródło" (silnik-list-filtr-nagłówka — `source` jest już
   * w whiteliście deskryptora `bare-document-list-descriptor.ts`, więc backend go sortuje
   * przez `autoSortColumns`). Mirror `resolveSortable` z `buildCoreColumns`/kolumny „sender"
   * (`Letters.tsx`) — ta kolumna jest budowana OSOBNO (wykluczona z `buildCoreColumns`, patrz
   * nagłówek pliku), więc trzeba wpiąć sort ręcznie. Pominięte = `sortable: false` — parytet
   * ze stanem sprzed poszerzenia (np. dla hipotetycznego nowego wołającego, który jeszcze nie
   * wpina resolvera sortu).
   */
  resolveSourceSortable?: (slug: string) => boolean;
  /**
   * Filtrowalność serwerowa kolumny „źródło" (mirror `resolveFilterable` z `buildCoreColumns`)
   * — gdy `true`, dokłada `headerFilter` (kategoria `select`) z opcjami = trzy wartości enuma
   * (`manual`/`import`/`api`) i ich etykietami z komórki (`sourceLabel`), więc wiersz filtrów ma
   * listę wyboru z ludzkimi nazwami zamiast pustej komórki. Wartość filtra = kod, jak w bazie.
   * Pominięte/`false` = BEZ `headerFilter` i BEZ pola „Szukaj…" (`filterable: false`) — kolumna
   * bez filtra ma pustą komórkę, nigdy martwe pole tekstowe.
   */
  resolveSourceFilterable?: (slug: string) => boolean;
  /**
   * Filtrowalność serwerowa kolumny „opiekun" (`meta.filterable` ostatniej odpowiedzi listy, mirror
   * `resolveSourceFilterable`). Razem z niepustym `ownerFilterOptions` daje filtr — lista rozwijana
   * z wyszukiwaniem (`record-select`, wartość = id użytkownika; wzorzec `buildCompanyColumn`).
   * Pominięte/`false` = bez filtra.
   */
  resolveOwnerFilterable?: (slug: string) => boolean;
  /**
   * Opcje filtra „opiekun": użytkownicy tenanta (`useListFilterOptions().ownerOptions`). PUSTE =
   * kolumna bez kontrolki filtra (pusta komórka), nie pole tekstowe, które nic nie filtruje — pusta
   * lista opcji uruchomiłaby też w popupie zapytanie o surowe uuid. Inwariant jak w `buildCompanyColumn`.
   */
  ownerFilterOptions?: ReadonlyArray<ListFilterOption>;
  /**
   * Opcjonalny tekst zastępczy dla opiekuna, którego etykiety serwer nie rozwiązał (brak klucza
   * w `extRefLabels`) — zamiast surowego id (patrz `resolveReferenceLabel`). Domyślnie wyłączony:
   * zwykłe listy dokumentów zachowują surowe id; włącza go tylko sekcja kokpitu decyzji.
   */
  unresolvedRefLabel?: string;
}

/**
 * Kolumny core wspólne list dokumentów: opiekun (`owner_user_id`) — core-referencja
 * rozwiązywana przez `extRefLabels` (patrz `renderCoreReferenceCell`) — i opcjonalnie
 * źródło (`source`, t-mapped label z fallbackiem do surowej wartości dla źródeł spoza
 * enumu `DOC_SOURCE_VALUES`).
 *
 * `Row` niesparametryzowany celowo (jak `buildExtColumns`) — `accessor`/`renderCell` w
 * `ColumnDefinition<T>` są typowane jako `string`/`(value: unknown, row: T) => ReactNode`,
 * bez ograniczenia `keyof T`, więc kolumny pasują do dowolnego kształtu wiersza
 * niosącego pola `ownerUserId`/`source` (każdy z 7 interfejsów Row na stronach list je
 * deklaruje).
 */
export function buildDocumentCoreRefColumns<Row>(
  args: BuildDocumentCoreRefColumnsArgs,
): ColumnDefinition<Row>[] {
  const {
    t,
    getRefLabels,
    withSource = false,
    resolveSourceSortable,
    resolveSourceFilterable,
    resolveOwnerFilterable,
    ownerFilterOptions = [],
    unresolvedRefLabel,
  } = args;
  const ownerFilterable =
    (resolveOwnerFilterable?.('owner_user_id') ?? false) && ownerFilterOptions.length > 0;
  const columns: ColumnDefinition<Row>[] = [
    {
      id: 'owner_user_id',
      accessor: 'ownerUserId',
      header: t('tenant.documents.columns.owner'),
      defaultVisible: true,
      defaultWidth: 180,
      sortable: false,
      // Kolumna referencyjna (uuid) — NIGDY nie ma generycznego pola „Szukaj…" (jak firma): filtr to
      // lista użytkowników (`record-select`, tylko w wierszu filtrów), a bez niej komórka jest pusta.
      filterable: false,
      renderCell: renderCoreReferenceCell(
        'owner_user_id',
        getRefLabels,
        t,
        'id',
        unresolvedRefLabel,
      ),
      ...(ownerFilterable
        ? {
            headerFilter: {
              category: 'record-select' as const,
              fieldSlug: 'owner_user_id',
              options: ownerFilterOptions.map((option) => ({ ...option })),
              // Lista użytkowników (do kilkuset pozycji) dołożona dla wiersza filtrów: jawny Kanban
              // po „Opiekunie" ma mieć tory tylko z danych, nie tor dla każdego użytkownika.
              kanbanFallback: false as const,
            },
          }
        : {}),
    },
  ];
  if (withSource) {
    columns.push({
      id: 'source',
      accessor: 'source',
      header: t('tenant.documents.shared.fields.source'),
      defaultVisible: true,
      defaultWidth: 120,
      // silnik-list-filtr-nagłówka: `source` jest już whitelisted w deskryptorze
      // (`bare-document-list-descriptor.ts`) — sort/filtr serwerowy istnieją na backendzie,
      // tej kolumnie brakowało tylko wpięcia po stronie frontu (patrz JSDoc
      // `resolveSourceSortable`/`resolveSourceFilterable` wyżej).
      sortable: resolveSourceSortable ? resolveSourceSortable('source') : false,
      // Bez filtra (serwer go nie serwuje / brak resolvera) komórka wiersza filtrów jest PUSTA —
      // nigdy generyczne pole „Szukaj…", które nic nie filtruje (patrz `resolveSourceFilterable`).
      filterable: false,
      renderCell: (value: unknown) => {
        if (value == null || value === '') return <span className="text-text-tertiary">—</span>;
        return <span className="text-text-secondary">{sourceLabel(String(value), t)}</span>;
      },
      ...(resolveSourceFilterable?.('source')
        ? {
            headerFilter: {
              category: 'select' as const,
              fieldSlug: 'source',
              fieldType: 'select',
              // Opcje = wartości enuma z etykietami komórki; `kanbanFallback: false` — dołożone dla
              // wiersza filtrów, nie zmieniają domyślnego pola grupującego Kanbana.
              options: codeFilterOptions([...DOC_SOURCE_VALUES], sourceLabel, t),
              kanbanFallback: false as const,
            },
          }
        : {}),
    });
  }
  return columns;
}
